import { FC, ReactNode, useState } from 'react'
import { AnimatePresence } from 'framer-motion'

import { FilterKey, useFiltersContext } from 'context/filters-context'
import { useAppContext } from 'context/app-context'
import {
  ClearButton,
  Cols,
  FilterItem,
  FilterToggle,
  Options,
  SubfilterToggle,
  Subfilters,
  Toggle,
  ToggleWrapper,
} from './styled'

type ComplexOption = { [k: string]: { count: number } }

type Props = {
  options: { [k: string]: number } | ComplexOption
  filterKey: FilterKey
  isOpen: boolean
  label: string
  renderSubfilters?: (value: string) => ReactNode
  onOpenToggle: (v: FilterKey | '') => void
}

const transitions = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
}

export const FilterOption: FC<Props> = ({
  options,
  filterKey,
  isOpen,
  label,
  renderSubfilters,
  onOpenToggle,
}) => {
  const { strings } = useAppContext()
  const { filters, setFilters } = useFiltersContext()
  const [expandedItem, setExpandedItem] = useState('')

  const handleRemoveFilter = () => {
    setFilters(f => {
      if (filterKey === 'year') {
        return { ...f, [filterKey]: '', month: '' }
      }

      if (filterKey === 'region') {
        return { ...f, [filterKey]: '', subregion: '' }
      }

      return { ...f, [filterKey]: '' }
    })
  }

  return (
    <FilterItem data-filter={filterKey} data-open={isOpen}>
      <ToggleWrapper>
        <Toggle onClick={() => onOpenToggle(isOpen ? '' : filterKey)}>
          {label}
        </Toggle>
        {!!filters[filterKey] && <ClearButton onClick={handleRemoveFilter} />}
      </ToggleWrapper>

      <Options
        initial={false}
        animate={{
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0,
        }}
      >
        <li>
          <FilterToggle
            disabled={!filters[filterKey]}
            onClick={handleRemoveFilter}
          >
            {strings.filters_all}
          </FilterToggle>
        </li>

        {Object.keys(options).map(option => (
          <li key={option}>
            {typeof renderSubfilters === 'function' &&
            !!renderSubfilters(option) ? (
              <Cols>
                <FilterToggle
                  disabled={
                    filters[filterKey] === option &&
                    (filterKey !== 'year' || !filters.month) &&
                    (filterKey !== 'region' || !filters.subregion)
                  }
                  onClick={() => {
                    if (filterKey === 'year') {
                      setFilters(f => ({
                        ...f,
                        [filterKey]: option,
                        month: '',
                      }))
                    } else if (filterKey === 'region') {
                      setFilters(f => ({
                        ...f,
                        [filterKey]: option,
                        subregion: '',
                      }))
                    } else {
                      setFilters(f => ({ ...f, [filterKey]: option }))
                    }
                  }}
                >
                  {option}
                </FilterToggle>
                <SubfilterToggle
                  data-expanded={expandedItem === option}
                  onClick={() =>
                    setExpandedItem(v => (v === option ? '' : option))
                  }
                >
                  {typeof options[option] === 'number'
                    ? (options[option] as number)
                    : (options[option] as ComplexOption[string]).count}
                </SubfilterToggle>
              </Cols>
            ) : (
              <FilterToggle
                disabled={filters[filterKey] === option}
                onClick={() => setFilters(f => ({ ...f, [filterKey]: option }))}
              >
                <span>{option}</span>{' '}
                <span>
                  {typeof options[option] === 'number'
                    ? (options[option] as number)
                    : (options[option] as ComplexOption[string]).count}
                </span>
              </FilterToggle>
            )}

            <AnimatePresence>
              {typeof renderSubfilters === 'function' &&
                expandedItem === option && (
                  <Subfilters {...transitions}>
                    {renderSubfilters(option)}
                  </Subfilters>
                )}
            </AnimatePresence>
          </li>
        ))}
      </Options>
    </FilterItem>
  )
}
